import React, { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getAllRefunds, refundAction } from "../../services/api";
import AdminLayout from "../layout/AdminLayout";
import {
  FaCheck,
  FaTimes,
  FaSearch,
  FaDownload,
  FaExclamationCircle,
} from "react-icons/fa";

const STATUS_META = {
  pending: { label: "Pending", badge: "bg-yellow-50 text-yellow-700 border border-yellow-200" },
  completed: { label: "Approved", badge: "bg-green-100 text-green-700" },
  failed: { label: "Rejected", badge: "bg-red-50 text-red-600" },
};

export default function RefundRequests({ canManage, Layout }) {
  const queryClient = useQueryClient();
  const LayoutComp = Layout || AdminLayout;

  const { data, isLoading } = useQuery({
    queryKey: ["admin-refunds"],
    queryFn: getAllRefunds,
    refetchInterval: 30000,
  });

  const refunds = data?.results || [];

  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("latest");
  const [selected, setSelected] = useState(null);
  const [declineReason, setDeclineReason] = useState("");
  const [showDeclineModal, setShowDeclineModal] = useState(null);

  const mutation = useMutation({
    mutationFn: ({ reference, action, reason }) => refundAction(reference, action, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-refunds"] });
      setShowDeclineModal(null);
      setDeclineReason("");
    },
  });

  const stats = useMemo(() => {
    const now = new Date();
    const thisMonth = (d) => {
      const dt = new Date(d);
      return dt.getMonth() === now.getMonth() && dt.getFullYear() === now.getFullYear();
    };
    return {
      pending: refunds.filter((r) => r.status === "pending").length,
      approvedThisMonth: refunds.filter((r) => r.status === "completed" && thisMonth(r.processed_at || r.created_at)).length,
      approvedAmount: refunds
        .filter((r) => r.status === "completed" && thisMonth(r.processed_at || r.created_at))
        .reduce((s, r) => s + parseFloat(r.total_amount || r.amount || 0), 0),
      rejectedThisMonth: refunds.filter((r) => r.status === "failed" && thisMonth(r.processed_at || r.created_at)).length,
    };
  }, [refunds]);

  const filtered = useMemo(() => {
    let list = refunds;
    const map = { pending: "pending", approved: "completed", rejected: "failed" };
    if (filter !== "all") list = list.filter((r) => r.status === map[filter]);
    if (search.trim()) {
      const term = search.toLowerCase();
      list = list.filter(
        (r) =>
          r.user?.toLowerCase().includes(term) ||
          r.id?.toLowerCase().includes(term) ||
          r.account_number?.includes(term),
      );
    }
    list = [...list].sort((a, b) =>
      sort === "latest"
        ? new Date(b.created_at) - new Date(a.created_at)
        : new Date(a.created_at) - new Date(b.created_at),
    );
    return list;
  }, [refunds, filter, search, sort]);

  const counts = {
    all: refunds.length,
    pending: refunds.filter((r) => r.status === "pending").length,
    approved: refunds.filter((r) => r.status === "completed").length,
    rejected: refunds.filter((r) => r.status === "failed").length,
  };

  const handleExportCsv = () => {
    const header = ["Patient", "Requested Amount", "Fee", "Total Requested", "Bank", "Account Number", "Status", "Date"];
    const rows = filtered.map((r) => [
      r.user,
      r.amount,
      r.fee,
      (parseFloat(r.amount || 0) + parseFloat(r.fee || 0)).toFixed(2),
      r.bank,
      r.account_number,
      STATUS_META[r.status]?.label || r.status,
      new Date(r.created_at).toLocaleString(),
    ]);
    const csv = [header, ...rows].map((row) => row.map((v) => `"${v ?? ""}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "refund-requests.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <LayoutComp>
      <div className="flex items-center justify-between mb-1">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Refund Requests</h1>
          <p className="text-gray-500 text-sm mt-1">
            Review, {canManage ? "approve, or reject" : "and view"} patient wallet refund requests.
            {!canManage && " (Read-only — Super Admin approval required to act.)"}
          </p>
        </div>
        <button
          onClick={handleExportCsv}
          className="flex items-center gap-2 text-sm font-semibold text-gray-700 border border-gray-200 px-4 py-2 rounded-xl hover:bg-gray-50 transition bg-white"
        >
          <FaDownload className="w-3 h-3" /> Export CSV
        </button>
      </div>

      {/* Stats */}
      <div className="grid sm:grid-cols-3 gap-5 mt-6 mb-6">
        <div className="bg-gradient-to-br from-[#0d3d2e] to-[#0f4a38] rounded-2xl p-5 text-white relative overflow-hidden">
          <div className="flex items-center justify-between mb-3">
            <div className="w-9 h-9 bg-white/20 rounded-lg flex items-center justify-center">
              <FaExclamationCircle className="w-4 h-4" />
            </div>
            {stats.pending > 0 && (
              <span className="text-[10px] font-bold bg-white/20 px-2 py-1 rounded-full">Needs action</span>
            )}
          </div>
          <p className="text-xs uppercase tracking-wide text-green-200 font-semibold mb-1">Pending Approval</p>
          <p className="text-3xl font-black">{stats.pending}</p>
        </div>

        <div className="bg-white rounded-2xl p-5 border border-gray-100">
          <div className="w-9 h-9 bg-green-50 rounded-lg flex items-center justify-center mb-3">
            <FaCheck className="text-green-600 w-4 h-4" />
          </div>
          <p className="text-xs uppercase tracking-wide text-gray-400 font-semibold mb-1">Approved This Month</p>
          <p className="text-3xl font-black text-gray-900">{stats.approvedThisMonth}</p>
          <p className="text-xs text-gray-400 mt-1">₦{stats.approvedAmount.toLocaleString()} approved</p>
        </div>

        <div className="bg-white rounded-2xl p-5 border border-gray-100">
          <div className="w-9 h-9 bg-red-50 rounded-lg flex items-center justify-center mb-3">
            <FaTimes className="text-red-500 w-4 h-4" />
          </div>
          <p className="text-xs uppercase tracking-wide text-gray-400 font-semibold mb-1">Rejected This Month</p>
          <p className="text-3xl font-black text-gray-900">{stats.rejectedThisMonth}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl border border-gray-100 p-4 mb-4 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          {[
            ["all", "All"],
            ["pending", "Pending"],
            ["approved", "Approved"],
            ["rejected", "Rejected"],
          ].map(([key, label]) => (
            <button
              key={key}
              onClick={() => setFilter(key)}
              className={`text-sm font-semibold px-3.5 py-1.5 rounded-lg border transition flex items-center gap-1.5 ${
                filter === key ? "border-[#0d3d2e] text-[#0d3d2e] bg-green-50" : "border-gray-200 text-gray-500 hover:border-gray-300"
              }`}
            >
              {label}
              <span className="text-[10px] bg-gray-100 text-gray-500 rounded-full px-1.5">{counts[key]}</span>
            </button>
          ))}
        </div>
        <div className="relative flex-1 min-w-[200px]">
          <FaSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 w-3 h-3" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search patient, reference..."
            className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
          />
        </div>
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          className="text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
        >
          <option value="latest">Sort: Latest first</option>
          <option value="oldest">Sort: Oldest first</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        {isLoading ? (
          <div className="text-center py-16 text-gray-400 text-sm">Loading refund requests...</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-gray-400 text-sm">No refund requests found.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px] text-sm">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="text-left py-3.5 px-5 font-semibold text-gray-500 text-xs uppercase tracking-wide">Patient</th>
                  <th className="text-left py-3.5 px-5 font-semibold text-gray-500 text-xs uppercase tracking-wide">Requested Amount</th>
                  <th className="text-left py-3.5 px-5 font-semibold text-gray-500 text-xs uppercase tracking-wide">Total Requested</th>
                  <th className="text-left py-3.5 px-5 font-semibold text-gray-500 text-xs uppercase tracking-wide">Status</th>
                  <th className="text-right py-3.5 px-5 font-semibold text-gray-500 text-xs uppercase tracking-wide">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => {
                  const meta = STATUS_META[r.status] || STATUS_META.pending;
                  const initials = (r.user || "??").split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);
                  const net = parseFloat(r.amount || 0) + parseFloat(r.fee || 0);
                  return (
                    <tr key={r.id} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/50 transition">
                      <td className="py-3.5 px-5">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 bg-[#0d3d2e] text-white rounded-full flex items-center justify-center text-xs font-bold shrink-0">
                            {initials}
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900">{r.user}</p>
                            <p className="text-xs text-gray-400">{r.account_number}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-3.5 px-5">
                        <p className="font-semibold text-gray-900">₦{parseFloat(r.amount || 0).toLocaleString()}</p>
                        <p className="text-xs text-gray-400">Fee: ₦{parseFloat(r.fee || 0).toLocaleString()}</p>
                      </td>
                      <td className="py-3.5 px-5 font-semibold text-gray-900">₦{net.toLocaleString()}</td>
                      <td className="py-3.5 px-5">
                        <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${meta.badge}`}>{meta.label}</span>
                        {r.status === "failed" && r.failure_reason && (
                          <p className="text-[11px] text-gray-400 mt-1 max-w-[160px] truncate" title={r.failure_reason}>{r.failure_reason}</p>
                        )}
                      </td>
                      <td className="py-3.5 px-5 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {canManage && r.status === "pending" && (
                            <>
                              <button
                                onClick={() => mutation.mutate({ reference: r.id, action: "approve" })}
                                disabled={mutation.isPending}
                                className="text-xs font-semibold bg-[#0d3d2e] hover:bg-[#0a3226] text-white px-3 py-1.5 rounded-lg transition flex items-center gap-1 disabled:opacity-50"
                              >
                                <FaCheck className="w-2.5 h-2.5" /> Approve
                              </button>
                              <button
                                onClick={() => setShowDeclineModal(r)}
                                disabled={mutation.isPending}
                                className="text-xs font-semibold bg-red-50 hover:bg-red-100 text-red-600 px-3 py-1.5 rounded-lg transition flex items-center gap-1 disabled:opacity-50"
                              >
                                <FaTimes className="w-2.5 h-2.5" /> Reject
                              </button>
                            </>
                          )}
                          <button
                            onClick={() => setSelected(r)}
                            className="text-xs font-semibold border border-gray-200 text-gray-600 px-3 py-1.5 rounded-lg hover:bg-gray-50 transition"
                          >
                            View
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* View modal */}
      {selected && (
        <div className="fixed inset-0 z-[999] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setSelected(null)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4">
            <h3 className="font-bold text-lg text-gray-900">Refund Request Details</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div><p className="text-xs text-gray-400">Patient</p><p className="font-semibold">{selected.user}</p></div>
              <div><p className="text-xs text-gray-400">Status</p><p className="font-semibold">{STATUS_META[selected.status]?.label}</p></div>
              <div><p className="text-xs text-gray-400">Requested</p><p className="font-semibold">₦{parseFloat(selected.amount || 0).toLocaleString()}</p></div>
              <div><p className="text-xs text-gray-400">Fee</p><p className="font-semibold">₦{parseFloat(selected.fee || 0).toLocaleString()}</p></div>
              <div><p className="text-xs text-gray-400">Bank</p><p className="font-semibold">{selected.bank}</p></div>
              <div><p className="text-xs text-gray-400">Account No.</p><p className="font-semibold">{selected.account_number}</p></div>
              <div className="col-span-2"><p className="text-xs text-gray-400">Account Name</p><p className="font-semibold">{selected.account_name}</p></div>
              {selected.description && (
                <div className="col-span-2"><p className="text-xs text-gray-400">Reason / Narration</p><p className="font-medium">{selected.description}</p></div>
              )}
              {selected.status === "failed" && selected.failure_reason && (
                <div className="col-span-2"><p className="text-xs text-gray-400">Rejection Reason</p><p className="font-medium text-red-600">{selected.failure_reason}</p></div>
              )}
              <div className="col-span-2"><p className="text-xs text-gray-400">Requested On</p><p className="font-medium">{new Date(selected.created_at).toLocaleString()}</p></div>
            </div>
            <button onClick={() => setSelected(null)} className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-2.5 rounded-xl transition">
              Close
            </button>
          </div>
        </div>
      )}

      {/* Decline reason modal */}
      {showDeclineModal && (
        <div className="fixed inset-0 z-[999] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowDeclineModal(null)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4">
            <h3 className="font-bold text-lg text-gray-900">Reject Refund Request</h3>
            <p className="text-sm text-gray-500">
              Provide a reason for rejecting {showDeclineModal.user}'s refund request. This will be shared with the patient.
            </p>
            <textarea
              value={declineReason}
              onChange={(e) => setDeclineReason(e.target.value)}
              rows={4}
              placeholder="e.g. Account details could not be verified"
              className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-red-400 resize-none"
            />
            <div className="flex gap-3">
              <button onClick={() => setShowDeclineModal(null)} className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-2.5 rounded-xl transition">
                Cancel
              </button>
              <button
                onClick={() =>
                  mutation.mutate({ reference: showDeclineModal.id, action: "decline", reason: declineReason || "Declined by Super Admin" })
                }
                disabled={mutation.isPending}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white font-semibold py-2.5 rounded-xl transition disabled:opacity-50"
              >
                {mutation.isPending ? "Rejecting..." : "Confirm Reject"}
              </button>
            </div>
          </div>
        </div>
      )}
    </LayoutComp>
  );
}
